<script setup>
const route = useRoute();
const gtm = useGtm();
const { t, locale } = useI18n();
const props = defineProps([
  'id',
  'date',
  'timeStampStart',
  'timeStampEnd',
  'eventName',
  'time',
  'durationDisplay',
  'locations',
  'more',
  'bold',
  'noButton',
  'noExtras',
  'noLink',
  'reservationType',
  'type',
  'categoryId',
  'hideBuildingOpening',
]);

const itemID = computed(() => `accordionItem-${props._uid}`);

const calendarUrl = () => {
  const startDate = new Date(props.time);
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1 < 10 ? '0' + (startDate.getMonth() + 1) : startDate.getMonth() + 1;
  const startDay = startDate.getDate() < 10 ? '0' + startDate.getDate() : startDate.getDate();
  const startHours = startDate.getHours() < 10 ? '0' + startDate.getHours() : startDate.getHours();
  const startMinutes = startDate.getMinutes() < 10 ? '0' + startDate.getMinutes() : startDate.getMinutes();

  const endDate = new Date(props.timeStampEnd);
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1 < 10 ? '0' + (endDate.getMonth() + 1) : endDate.getMonth() + 1;
  const endDay = endDate.getDate() < 10 ? '0' + endDate.getDate() : endDate.getDate();
  const endHours = endDate.getHours() < 10 ? '0' + endDate.getHours() : endDate.getHours();
  const endMinutes = endDate.getMinutes() < 10 ? '0' + endDate.getMinutes() : endDate.getMinutes();

  let location = '';
  if (props.locations.length) {
    props.locations.forEach((element) => {
      if (element.name) {
        location += element.name + ' ';
      }
    });
  }

  return `https://calendar.google.com/calendar/r/eventedit?text=${props.eventName}&dates=${startYear}${startMonth}${startDay}T${startHours}${startMinutes}/${endYear}${endMonth}${endDay}T${endHours}${endMinutes}&details=&location=${location}`;
};

const openingHours = (data) => {
  const merged = [];
  let idx = -1;
  for (let i = 0; i < data.length; i++) {
    const day = data[i];
    if (idx === -1 || merged[idx].opensAt !== day.from) {
      merged.push({
        days: [t('global.weekdays.day-' + day.day_in_week)],
        opensAt: day.from,
        closesAt: day.to,
      });
      idx++;
    } else {
      merged[idx].days.push(t('global.weekdays.day-' + day.day_in_week));
    }
  }
  return merged.map((t) => ({
    days: t.days.length === 1 ? t.days[0] : t.days[0] + '–' + t.days[t.days.length - 1],
    opensAt: t.opensAt.split(':').slice(0, 2).join(':'),
    closesAt: t.closesAt.split(':').slice(0, 2).join(':'),
  }));
};

const track = (reservationType) => {
  if (reservationType === 'GOOUT' || reservationType === 'NONE' || !reservationType) {
  } else {
    gtm.trackEvent({
      event: 'eventDetail_reservationOpen',
      eventId: route.params.id || route.params.eventId,
      url: window.location.href.replace(window.location.origin, ''),
    });
  }
};

const moreButtonFilter = (val) => {
  if (val === 1) {
    return `+ ${val} ${t('pages.event-detail.more-1')}`;
  } else if (val < 5) {
    return `+ ${val} ${t('pages.event-detail.more-2')}`;
  } else {
    return `+ ${val} ${t('pages.event-detail.more-3')}`;
  }
};
</script>

<template>
  <div class="event-date-strip" v-if="date?.end_at?.timestamp > Math.floor(Date.now() / 1000)">
    <div class="event-date-strip__main">
      <div class="event-date-strip__meta">
        <div class="center b-flex">
          <!-- datum -->

          <div xl v-if="date && type !== 'exposition' && checkDuration(63072000, date)" lg class="b-flex-child">
            <div v-if="noLink" :class="{ fwm: bold }"><span class="icon-calendar"></span>{{ typeof date === 'string' ? date : useDateRange([date]) }}</div>
            <template v-else>
              <span class="icon-calendar"></span>
              <nuxt-link
                :to="
                  localePath({
                    name: 'event-id-slug-dateId',
                    params: {
                      id: route.params.id,
                      slug: route.params.slug,
                      dateId: route.params.dateId || route.params.ticketId || id || null,
                    },
                  })
                "
                :class="{ fwm: bold }"
                class="link"
              >
                {{ typeof date === 'string' ? date : useDateRange([date]) }}
              </nuxt-link>
            </template>
            <button v-if="more && !noExtras" class="event-date-strip__more mt-1" @click="toggleMore" @keyup.enter="toggleMore">
              {{ moreButtonFilter(more) }}
            </button>
          </div>

          <!-- cas -->
          <div v-if="time && type !== 'exposition' && type !== 'exhibition' && checkDuration(63072000, date)" xl class="mt-2 mt-xl-0 b-flex-child">
            <div class="event-date-strip__time">
              <span class="icon-time"></span>
              <div>
                <span :class="{ fwm: bold }"> {{ timeStampEnd ? `${useTimeFormat(time)}–${useTimeFormat(timeStampEnd)}` : `${useTimeFormat(time)}` }}</span
                ><br />
                <span v-if="durationDisplay && checkDuration(86400, date)">({{ useGetDuration(date) }})</span>
              </div>
            </div>
          </div>

          <!--lokace-->
          <div v-if="false" xl class="mt-2 mt-xl-0 b-flex-child">
            <div class="event-date-strip__locations">
              <img src="/img/calender-add.svg" class="mr-2" alt="calendar icon" />
              <span>
                <span v-for="(location, index) in locations" :key="index" class="event-date-strip__location">
                  <a target="_blank" :href="calendarUrl" class="link">
                    <strong>{{ t('pages.event-detail.add-to-calendar') }}</strong>
                  </a>
                </span>
              </span>
            </div>
          </div>

          <!-- budova -->
          <div xl class="mt-2 mt-xl-0 b-flex-child" :class="{ fwm: bold }">
            <div v-if="locations?.length" class="event-date-strip__locations">
              <span class="icon-pin"></span>
              <span>
                <span v-for="(location, index) in locations" :key="index" class="event-date-strip__location">
                  <nuxt-link
                    :to="
                      localePath({
                        name: 'about-buildings-slug',
                        params: { slug: location.slug[locale] },
                      })
                    "
                    class="link"
                  >
                    {{ location.name }}
                  </nuxt-link>
                  <template v-if="locations.length > 0 && index != locations.length - 1">,&ensp; </template>
                </span>
              </span>
            </div>
          </div>
          <!-- oteviraci doba-->
          <div v-if="locations && !hideBuildingOpening" xl class="mt-2 mt-xl-0 b-flex-child">
            <div v-for="(location, index) in locations" :key="index">
              <div v-if="location.openingHours">
                <div v-for="(item, i) in openingHours(location.openingHours)" :key="i">
                  <div class="event-date-strip__openeinghours">
                    <span class="icon-time"></span>
                    <span>
                      <strong>{{ item.days }}: </strong>
                      <br />
                      <span>{{ item.opensAt }}–{{ item.closesAt }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div lg="auto" class="mt-2 mt-xl-0 b-flex-child">
            <nuxt-link
              v-if="!noButton"
              id="ticket"
              :to="
                localePath({
                  name: 'event-id-slug-ticketId-tickets',
                  params: {
                    id: route.params.id,
                    slug: route.params.slug,
                    ticketId: route.params.ticketId || route.params.dateId || id || null,
                  },
                })
              "
              class="button button--medium button--ticket"
              @click="track(reservationType)"
            >
              {{ reservationType == 'GOOUT' || reservationType == 'NONE' || !reservationType ? t('pages.event-detail.tickets') : t('pages.event-detail.reservations') }}
            </nuxt-link>
          </div>
        </div>
        <p id="ticket-description" class="ticket-description">Vstupenky v prodeji od 1.8.2022 12:00h</p>
      </div>
    </div>
    <div class="event-date-strip__arrow" />
  </div>
</template>

<style lang="scss" scoped>
.event-date-strip {
  display: flex;

  &__main {
    width: 100%;
  }

  &__arrow {
  }

  &__title {
    font-weight: $font-weight-medium;
    font-size: 14px;
    line-height: 1.5;
  }

  &__meta {
    padding: 15px 0;
  }

  &__more {
    display: block;
    cursor: pointer;
    @include button-reset;
  }

  &__locations {
    display: flex;

    @include mq($lg) {
      align-items: center;
    }

    [class^='icon-'] {
      flex-shrink: 0;

      @include mq($lg, true) {
        margin-right: 7px !important;
      }
    }
  }
  &__location {
    display: inline-block;
    padding-bottom: 6px;
  }
  &__openeinghours {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    line-height: 1.3rem;
  }

  [class^='icon-'],
  [class*=' icon-'] {
    font-size: 22px;
    margin-right: 5px;
    position: relative;
    text-align: center;
    top: -2px;
    vertical-align: middle;

    @include mq($lg, true) {
      width: 20px;
    }
  }
  .icon-location {
    top: 1px;
  }

  .ticket-description {
    display: none;

    text-align: left;
    padding-top: 15px;

    @include mq($lg) {
      text-align: right;
    }
  }
}

.b-flex {
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  @media (max-width: 1200px) {
    display: block;
  }
}

.b-flex-child {
  width: auto;
  min-width: auto;
  max-width: 180px;
  flex: 0 0 auto;
  margin-right: 0;
  @media (max-width: 1200px) {
    max-width: 100%;
  }
}

.event-date-strip {
  border-bottom: 0.5px solid rgba($black, 0.3);
  border-top: 0.5px solid rgba($black, 0.3);
}

.event-date-strip__time {
  display: flex;
  align-items: center;
  line-height: 1.3;
}
</style>
